import { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
	addMtdCommentRequest,
	getMtdReportRequest,
	getMtdReportsRequest,
	sendMtdFileReportRequest,
} from 'redux/reducers/hmrc/reducer';
import {
	IMtdEmReportsItem,
	TMtdReportsItemResponse,
	TSendMtdFileReportData,
} from 'redux/reducers/hmrc/types';
import { getMtdReport, getMtdReports, getMtdReportsLoad } from 'redux/reducers/hmrc/selectors';
import queryString from 'query-string';
import { TFilters, TOrderBy } from 'redux/types';
import { PAGE_SIZE } from 'services/constants/env';
import { ESortDirection, THandleAction } from 'ui/EasTable/types';
import { EasVatNumberReports } from './EasVatNumberReports';
import { ReportDetails } from './ReportDetails';

import { easReportsStructure } from './data/eas-reports-structure';
import { CommentDialog } from './CommentDialog';
import { FileReportDialog } from './FileReportDialog';
import { TypeOfDialog } from './types';
import { getFiltersFromNavigateParams } from '../../utils/FilterParams';

export const HMRC: FC = () => {
	const dispatch = useDispatch();
	const params = useParams<{ id?: string }>();
	const { search } = useLocation();
	const tableStructure = easReportsStructure;

	const easTableStructure = easReportsStructure;
	const itemsIsLoading = useSelector(getMtdReportsLoad);

	const items = useSelector(getMtdReports);
	const mtdReport = useSelector(getMtdReport);
	const location = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(easTableStructure, navigateParams);

	const getOrderby = () => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC
					: ESortDirection.DESC,
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: ESortDirection.DESC,
		} as TOrderBy);

		return orderBy;
	};

	const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const handleCloseCommentsDialog = () => {
		setOpenCommentsDialog(false);
	};
	const handleAddComments = (text: string) => {
		if (items === null) return;
		dispatch(
			addMtdCommentRequest({
				id: selectedId ?? 0,
				comment: text,
				getMtdReportsRequest: {
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					filters: navigateParamsWithoutPage as TFilters,
					searchString: String(navigateParams?.search ?? ''),
					orderBy: getOrderby(),
				},
			}),
		);
		handleCloseCommentsDialog();
	};

	const [openFileReportDialog, setOpenFileReportDialog] = useState(false);
	const [fileReportId, setFileReportId] = useState<number | null>(null);
	const handleOpenFileReportDialog = (id: number) => {
		setFileReportId(id);
		setOpenFileReportDialog(true);
	};
	const handleCloseFileReportDialog = () => {
		setOpenFileReportDialog(false);
	};
	const handleFileReport = (data: TSendMtdFileReportData) => {
		if (items === null) return;
		if (!params?.id) {
			const item = items.data?.find((i) => i.id === fileReportId);
			console.log('text', item?.comments);
		}
		dispatch(sendMtdFileReportRequest({ id: fileReportId ?? 0, data }));
		handleCloseFileReportDialog();
	};

	useEffect(() => {
		const orderBy = getOrderby();

		dispatch(
			getMtdReportsRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				filters: navigateParamsWithoutPage as TFilters,
				searchString: String(navigateParams?.search ?? ''),
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, location.search, params?.id]);

	const handleAction: THandleAction = (id, typeOfDialog) => {
		setSelectedId(id);
		if (typeOfDialog === TypeOfDialog.COMMENT_DIALOG) {
			setOpenCommentsDialog(true);
		}
		if (typeOfDialog === TypeOfDialog.FILE_REPORT_DIALOG) {
			dispatch(getMtdReportRequest({ id }));
			setOpenFileReportDialog(true);
		}
	};

	return (
		<Box>
			{!params?.id && (
				<EasVatNumberReports<TMtdReportsItemResponse<IMtdEmReportsItem>, IMtdEmReportsItem>
					tableStructure={easTableStructure}
					items={items}
					itemsIsLoading={itemsIsLoading}
					handleAction={handleAction}
				/>
			)}
			{params?.id && (
				<ReportDetails<TMtdReportsItemResponse<IMtdEmReportsItem>, IMtdEmReportsItem>
					items={items}
					itemsIsLoading={itemsIsLoading}
					handleAction={handleAction}
				/>
			)}
			<CommentDialog
				comments={items?.data?.find((i) => i.id === selectedId)?.comments}
				open={openCommentsDialog}
				setClose={handleCloseCommentsDialog}
				handleAddComment={handleAddComments}
			/>
			<FileReportDialog
				item={!params?.id ? items?.data?.find((i) => i.id === selectedId) : mtdReport || undefined}
				id={selectedId}
				open={openFileReportDialog}
				setClose={handleCloseFileReportDialog}
				mtdReportsRequestData={{
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					filters: navigateParamsWithoutPage as TFilters,
					searchString: String(navigateParams?.search ?? ''),
					orderBy: getOrderby(),
				}}
			/>
		</Box>
	);
};
